import { supabase } from "../supabaseClient";

const TIPOS_ENVIAVEIS = ["Atas", "Tarefas", "Lista", "Metas", "Nota Rápida"];

export function podeEnviarNota(nota) {
  return TIPOS_ENVIAVEIS.includes(nota?.tipo);
}

async function montarSnapshot(notaId) {
  const { data: nota } = await supabase.from("notas").select("*").eq("id", notaId).single();
  if (!nota) throw new Error("Nota não encontrada.");

  const { data: anexos } = await supabase.from("anexos").select("*").eq("nota_id", notaId);
  const { data: checklists } = await supabase.from("checklists").select("*").eq("nota_id", notaId);

  let checklistItems = [];
  let itemComentarios = [];
  let itemReprogramacoes = [];
  if (checklists?.length) {
    const checklistIds = checklists.map(c => c.id);
    const { data: items } = await supabase.from("checklist_items").select("*").in("checklist_id", checklistIds);
    checklistItems = items || [];
    if (checklistItems.length) {
      const itemIds = checklistItems.map(i => i.id);
      const { data: coms } = await supabase.from("checklist_item_comentarios").select("*").in("checklist_item_id", itemIds);
      const { data: reprog } = await supabase.from("checklist_item_reprogramacoes").select("*").in("checklist_item_id", itemIds);
      itemComentarios = coms || [];
      itemReprogramacoes = reprog || [];
    }
  }

  const { data: compromissos } = await supabase.from("compromissos").select("*").eq("nota_id", notaId);

  // ── Metas (+ responsáveis, anexos, comentários) ──
  let metas = [], metasResponsaveis = [], metasAnexos = [], metasComentarios = [];
  const { data: metasRaw } = await supabase
    .from("metas").select("*").eq("nota_id", notaId).order("ordem", { ascending: true });
  metas = metasRaw || [];
  if (metas.length) {
    const metaIds = metas.map(m => m.id);
    const [{ data: r }, { data: a }, { data: c }] = await Promise.all([
      supabase.from("metas_responsaveis").select("*").in("meta_id", metaIds),
      supabase.from("metas_anexos").select("*").in("meta_id", metaIds),
      supabase.from("metas_comentarios").select("*").in("meta_id", metaIds),
    ]);
    metasResponsaveis = r || [];
    metasAnexos = a || [];
    metasComentarios = c || [];
  }

  return {
    nota, anexos: anexos || [], checklists: checklists || [], checklistItems,
    itemComentarios, itemReprogramacoes, compromissos: compromissos || [],
    metas, metasResponsaveis, metasAnexos, metasComentarios,
  };
}

// ── Duplica todo o conteúdo relacionado (anexos, checklists, itens, comentários,
// reprogramações, compromissos, metas) de um snapshot para uma nota recém-criada.
// Extraído para ser reaproveitado tanto na aprovação de solicitações (cross-container)
// quanto no envio direto dentro do mesmo container.
async function duplicarConteudoRelacionado(snapshot, novaNotaId, { forcarContainerId } = {}) {
  const { anexos, checklists, checklistItems, itemComentarios, itemReprogramacoes, compromissos } = snapshot;

  if (anexos.length) {
    const novosAnexos = anexos.map(({ id, nota_id, comentario_id, ...rest }) => ({ ...rest, nota_id: novaNotaId }));
    const { error } = await supabase.from("anexos").insert(novosAnexos);
    if (error) throw error;
  }

  if (checklists.length) {
    const mapaChecklistIds = {};
    for (const cl of checklists) {
      const { id: oldClId, nota_id, ...clRest } = cl;
      const payload = { ...clRest, nota_id: novaNotaId };
      if (forcarContainerId) payload.container_id = forcarContainerId;
      const { data: novaChecklist, error } = await supabase.from("checklists").insert([payload]).select().single();
      if (error) throw error;
      mapaChecklistIds[oldClId] = novaChecklist.id;
    }

    const mapaItemIds = {};
    for (const item of checklistItems) {
      const { id: oldItemId, checklist_id, check1_user_id, check1_at, check2_user_id, check2_at, ...itemRest } = item;
      const { data: novoItem, error } = await supabase
        .from("checklist_items")
        .insert([{ ...itemRest, checklist_id: mapaChecklistIds[checklist_id] }])
        .select()
        .single();
      if (error) throw error;
      mapaItemIds[oldItemId] = novoItem.id;
    }

    if (itemComentarios.length) {
      const novosComentarios = itemComentarios
        .filter(c => mapaItemIds[c.checklist_item_id])
        .map(({ id, checklist_item_id, ...rest }) => ({ ...rest, checklist_item_id: mapaItemIds[checklist_item_id] }));
      if (novosComentarios.length) {
        const { error } = await supabase.from("checklist_item_comentarios").insert(novosComentarios);
        if (error) throw error;
      }
    }
    if (itemReprogramacoes.length) {
      const novasReprog = itemReprogramacoes
        .filter(r => mapaItemIds[r.checklist_item_id])
        .map(({ id, checklist_item_id, ...rest }) => ({ ...rest, checklist_item_id: mapaItemIds[checklist_item_id] }));
      if (novasReprog.length) {
        const { error } = await supabase.from("checklist_item_reprogramacoes").insert(novasReprog);
        if (error) throw error;
      }
    }
  }

  if (compromissos.length) {
    const novosCompromissos = compromissos.map(({ id, nota_id, ...rest }) => ({ ...rest, nota_id: novaNotaId }));
    const { error } = await supabase.from("compromissos").insert(novosCompromissos);
    if (error) throw error;
  }

  // ── Metas ──
  const metas = snapshot.metas || [];
  if (metas.length) {
    const metasResponsaveis = snapshot.metasResponsaveis || [];
    const metasAnexos = snapshot.metasAnexos || [];
    const metasComentarios = snapshot.metasComentarios || [];
    const mapaMetaIds = {};

    for (const m of metas) {
      const { id: oldMetaId, nota_id, ...metaRest } = m;
      const { data: novaMeta, error } = await supabase
        .from("metas").insert([{ ...metaRest, nota_id: novaNotaId }]).select().single();
      if (error) throw error;
      mapaMetaIds[oldMetaId] = novaMeta.id;
    }

    const novosResp = metasResponsaveis
      .filter(r => mapaMetaIds[r.meta_id])
      .map(({ id, meta_id, ...rest }) => ({ ...rest, meta_id: mapaMetaIds[meta_id] }));
    if (novosResp.length) {
      const { error } = await supabase.from("metas_responsaveis").insert(novosResp);
      if (error) throw error;
    }

    const novosComent = metasComentarios
      .filter(c => mapaMetaIds[c.meta_id])
      .map(({ id, meta_id, ...rest }) => ({ ...rest, meta_id: mapaMetaIds[meta_id] }));
    if (novosComent.length) {
      const { error } = await supabase.from("metas_comentarios").insert(novosComent);
      if (error) throw error;
    }

    // Anexos: copia o arquivo no storage; se falhar, reaproveita o arquivo original
    for (const a of metasAnexos) {
      const novaMetaId = mapaMetaIds[a.meta_id];
      if (!novaMetaId) continue;
      const { id, meta_id, ...rest } = a;
      let payload = { ...rest, meta_id: novaMetaId };
      const ext = a.nome_arquivo?.split(".").pop() || "bin";
      const novoPath = `objetivos/${novaMetaId}/${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext}`;
      const { error: copyErr } = await supabase.storage.from("ata-anexos").copy(a.storage_path, novoPath);
      if (!copyErr) {
        const { data: urlData } = supabase.storage.from("ata-anexos").getPublicUrl(novoPath);
        payload = { ...payload, storage_path: novoPath, url: urlData.publicUrl };
      } else {
        console.warn("Cópia do arquivo falhou, reaproveitando o original:", copyErr);
      }
      const { error } = await supabase.from("metas_anexos").insert([payload]);
      if (error) throw error;
    }
  }
}

export async function criarSolicitacaoEnvio({
  nota, usuarioId, containerOrigemId, containerDestinoId,
  projetoDestinoId, setorDestinoId, pilhaDestino, tipoOperacao,
}) {
  const snapshot = await montarSnapshot(nota.id);

  const { data: solicitacao, error } = await supabase
    .from("solicitacoes_envio_notas")
    .insert([{
      nota_id: nota.id,
      remetente_id: usuarioId,
      destinatario_id: containerDestinoId,
      container_origem_id: containerOrigemId || null,
      container_destino_id: containerDestinoId,
      projeto_destino_id: projetoDestinoId || null,
      setor_destino_id: setorDestinoId || null,
      pilha_destino_id: pilhaDestino.id,
      tipo_operacao: tipoOperacao,
      status: "pendente",
      snapshot,
    }])
    .select()
    .single();
  if (error) throw error;

  const { data: remetente } = await supabase.from("profiles").select("nickname, nome").eq("id", usuarioId).single();
  const nomeRemetente = remetente?.nickname || remetente?.nome || "Um colaborador";
  const acao = tipoOperacao === "mover" ? "mover" : "copiar";

  const { data: notif, error: notifError } = await supabase.from("notificacoes").insert([{
    user_id: containerDestinoId,
    remetente_id: usuarioId,
    tipo: "solicitacao_envio_nota",
    mensagem: `${nomeRemetente} quer ${acao} a nota "${nota.nome}" para a pilha "${pilhaDestino.title}".`,
    tipo_nota: nota.tipo,
    pilha_id: pilhaDestino.id,
    nota_id: nota.id,
    solicitacao_envio_id: solicitacao.id,
    lido: false,
  }]).select().single();
  if (notifError) throw notifError;

  // O trigger BEFORE INSERT sobrescreve container_id/projeto_id/setor_id com base
  // no nota_id (projeto de origem). Forçamos aqui os dados do DESTINO via UPDATE,
  // que não aciona o trigger.
  const { error: fixError } = await supabase.from("notificacoes").update({
    container_id: containerDestinoId,
    projeto_id: projetoDestinoId || null,
    setor_id: setorDestinoId || null,
    pilha_id: pilhaDestino.id,
  }).eq("id", notif.id);
  if (fixError) throw fixError;

  return solicitacao;
}

export async function listarSolicitacoesPendentes(usuarioId) {
  const { data, error } = await supabase
    .from("solicitacoes_envio_notas")
    .select("*")
    .eq("destinatario_id", usuarioId)
    .eq("status", "pendente")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function aprovarSolicitacaoEnvio(solicitacaoId) {
  const { data: sol, error } = await supabase.from("solicitacoes_envio_notas").select("*").eq("id", solicitacaoId).single();
  if (error || !sol) throw error || new Error("Solicitação não encontrada.");
  if (sol.status !== "pendente") return sol;

  if (sol.tipo_operacao === "mover") {
    const { error: moveErr } = await supabase.from("notas").update({ pilha_id: sol.pilha_destino_id }).eq("id", sol.nota_id);
    if (moveErr) throw moveErr;
  } else {
    const { nota, ...resto } = sol.snapshot;
    const { id: _oldId, ...notaSemId } = nota;
    const { data: novaNota, error: notaErr } = await supabase
      .from("notas")
      .insert([{ ...notaSemId, pilha_id: sol.pilha_destino_id, ordem: 0 }])
      .select()
      .single();
    if (notaErr) throw notaErr;

    try {
      await duplicarConteudoRelacionado(sol.snapshot, novaNota.id, { forcarContainerId: sol.container_destino_id });
    } catch (err) {
      await supabase.from("notas").delete().eq("id", novaNota.id);
      throw err;
    }
  }

  const { data: pilha } = await supabase.from("pilhas").select("title").eq("id", sol.pilha_destino_id).single();
  const acao = sol.tipo_operacao === "mover" ? "moveu" : "copiou";
  const nomeNota = sol.snapshot?.nota?.nome || "a nota";

  await supabase.from("solicitacoes_envio_notas").update({ status: "aceito", resolved_at: new Date().toISOString() }).eq("id", solicitacaoId);
  await supabase.from("notificacoes").update({
    tipo: "solicitacao_envio_resolvida",
    mensagem: `Você ${acao} a nota "${nomeNota}" para a pilha "${pilha?.title || "destino"}".`,
    lido: true,
  }).eq("solicitacao_envio_id", solicitacaoId);

  return sol;
}

export async function recusarSolicitacaoEnvio(solicitacaoId) {
  const { data: sol } = await supabase.from("solicitacoes_envio_notas").select("*").eq("id", solicitacaoId).single();

  await supabase.from("solicitacoes_envio_notas").update({ status: "recusado", resolved_at: new Date().toISOString() }).eq("id", solicitacaoId);

  const nomeNota = sol?.snapshot?.nota?.nome || "a nota";
  await supabase.from("notificacoes").update({
    tipo: "solicitacao_envio_resolvida",
    mensagem: `Você recusou receber a nota "${nomeNota}".`,
    lido: true,
  }).eq("solicitacao_envio_id", solicitacaoId);
}

// ═══════════════════════════════════════════════════════════════════════
// ENVIO DIRETO DENTRO DO MESMO CONTAINER (sem aprovação do dono)
//
// Regras de permissão (decididas com o usuário):
// - Se usuarioId === containerId → é o dono do container: pode enviar de/para
//   qualquer projeto ou setor daquele container.
// - Caso contrário → é colaborador: só pode enviar para projetos/setores onde
//   tem uma linha em permissoes_colaboradores para aquele container.
// ═══════════════════════════════════════════════════════════════════════

// Lista os projetos/setores do container que o usuário pode usar como destino
// (já excluindo a entidade atual, se informada).
export async function listarDestinosMesmoContainer({ usuarioId, containerId, entidadeAtualTipo, entidadeAtualId }) {
  const souDono = usuarioId === containerId;

  let projetos = [];
  let setores = [];

  if (souDono) {
    const [{ data: p }, { data: s }] = await Promise.all([
      supabase.from("projects").select("id, name, nickname").eq("user_id", containerId).eq("ativo", true).order("name", { ascending: true }),
      supabase.from("setores").select("id, name, nickname").eq("user_id", containerId).eq("ativo", true).order("name", { ascending: true }),
    ]);
    projetos = p || [];
    setores = s || [];
  } else {
    const { data: permissoes } = await supabase
      .from("permissoes_colaboradores")
      .select("projeto_id, setor_id")
      .eq("colaborador_id", usuarioId)
      .eq("container_id", containerId);

    const projetoIds = [...new Set((permissoes || []).map(p => p.projeto_id).filter(Boolean))];
    const setorIds = [...new Set((permissoes || []).map(p => p.setor_id).filter(Boolean))];

    const [{ data: p }, { data: s }] = await Promise.all([
      projetoIds.length ? supabase.from("projects").select("id, name, nickname").in("id", projetoIds) : Promise.resolve({ data: [] }),
      setorIds.length ? supabase.from("setores").select("id, name, nickname").in("id", setorIds) : Promise.resolve({ data: [] }),
    ]);
    projetos = p || [];
    setores = s || [];
  }

  const lista = [
    ...projetos.map(p => ({ ...p, tipo: "project" })),
    ...setores.map(s => ({ ...s, tipo: "setor" })),
  ];

  if (entidadeAtualTipo && entidadeAtualId != null) {
    return lista.filter(e => !(e.tipo === entidadeAtualTipo && String(e.id) === String(entidadeAtualId)));
  }
  return lista;
}

// Move ou copia a nota imediatamente para a pilha de destino, sem passar por
// solicitação/aprovação. Usado quando origem e destino estão no mesmo container.
export async function enviarNotaDiretamente({ nota, pilhaDestino, tipoOperacao }) {
  if (tipoOperacao === "mover") {
    const { error } = await supabase.from("notas").update({ pilha_id: pilhaDestino.id, ordem: 0 }).eq("id", nota.id);
    if (error) throw error;
    return { tipoOperacao, notaId: nota.id };
  }

  const snapshot = await montarSnapshot(nota.id);
  const { id: _oldId, ...notaSemId } = snapshot.nota;
  const { data: novaNota, error: notaErr } = await supabase
    .from("notas")
    .insert([{ ...notaSemId, pilha_id: pilhaDestino.id, ordem: 0 }])
    .select()
    .single();
  if (notaErr) throw notaErr;

  try {
    await duplicarConteudoRelacionado(snapshot, novaNota.id);
  } catch (err) {
    await supabase.from("notas").delete().eq("id", novaNota.id);
    throw err;
  }

  return { tipoOperacao, notaId: nota.id, novaNota };
}